import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { KeycloakService } from 'keycloak-angular';
import { JWTContent } from 'src/app/app.component';
import { ImpactPanelService } from 'src/app/shared/services/impact-panel.service';
import {
  MemberCollaboration,
  MemberCommitment,
  MemberInfo,
  MemberSummary,
  HistoryEvent,
} from 'src/app/shared/models/impact-panel.model';
import {
  ActivityFeed,
  ActivityIconKind,
  assignedStatusMeta,
  buildActivityFeed,
  categoryLabel,
  collaborationStatusMeta,
  collectTitleMaps,
  eventDotClass,
  eventIconKind,
  eventLabel,
  eventStatusChip,
  fmtDate,
  fmtDateTime,
  fmtTime,
  isGhostMemberCollaboration,
  shortAddr,
} from 'src/app/shared/utils/impact-panel.utils';

type ImpactPanelTab = 'compromisos' | 'colaboraciones';
type SortOrder = 'recent' | 'oldest' | 'az';
type SectionState = 'idle' | 'loading' | 'ready' | 'error';

interface FilterOption {
  value: string;
  label: string;
}

// Devuelve el último resultado mientras las entradas (por referencia) no cambien.
class Memo<T> {
  private keys: unknown[] | null = null;
  private value!: T;

  get(keys: unknown[], compute: () => T): T {
    const prev = this.keys;
    if (prev && prev.length === keys.length && keys.every((key, i) => key === prev[i])) {
      return this.value;
    }
    this.value = compute();
    this.keys = keys;
    return this.value;
  }
}

const LIST_PAGE_SIZE = 20;
const HISTORY_PAGE_SIZE = 20;
const ACTIVITY_PAGE_SIZE = 15;

@Component({
  selector: 'app-impact-panel',
  templateUrl: './impact-panel.page.html',
  styleUrls: ['./impact-panel.page.scss'],
})
export class ImpactPanelPage implements OnInit, OnDestroy {
  loading = true;
  noWallet = false;
  loadError = false;

  member: MemberInfo | null = null;
  walletAddress = '';
  summary: MemberSummary | null = null;
  commitments: MemberCommitment[] = [];
  collaborations: MemberCollaboration[] = [];
  activity: ActivityFeed = { shown: [], hiddenCount: 0, total: 0 };

  activeTab: ImpactPanelTab = 'compromisos';
  copied = false;

  commitmentSearch = '';
  commitmentStatusFilter = '';
  commitmentCommunityFilter = '';
  commitmentSort: SortOrder = 'recent';

  collaborationSearch = '';
  collaborationStatusFilter = '';
  collaborationCategoryFilter = '';
  collaborationSort: SortOrder = 'recent';

  readonly activityPageSize = ACTIVITY_PAGE_SIZE;

  // Las listas largas se montan de a LIST_PAGE_SIZE tarjetas (son expandibles, no sirve el
  // scroll virtual de altura fija).
  commitmentLimit = LIST_PAGE_SIZE;
  collaborationLimit = LIST_PAGE_SIZE;

  summaryState: SectionState = 'loading';
  commitmentsState: SectionState = 'loading';
  collaborationsState: SectionState = 'idle';
  historyState: SectionState = 'loading';

  historyHasMore = false;
  loadingMoreHistory = false;
  historyTotalEvents = 0;
  activityPage = 1;

  private historyEvents: HistoryEvent[] = [];
  private historyOffset = 0;
  private pendingActivityPage: number | null = null;
  private collabTitles: Record<string, string> = {};
  private commitmentTitles: Record<string, string> = {};
  private panelSub = new Subscription();
  private commitmentStatusOptionsMemo = new Memo<FilterOption[]>();
  private commitmentCommunityOptionsMemo = new Memo<FilterOption[]>();
  private filteredCommitmentsMemo = new Memo<MemberCommitment[]>();
  private visibleCommitmentsMemo = new Memo<MemberCommitment[]>();
  private collaborationStatusOptionsMemo = new Memo<FilterOption[]>();
  private collaborationCategoryOptionsMemo = new Memo<FilterOption[]>();
  private filteredCollaborationsMemo = new Memo<MemberCollaboration[]>();
  private visibleCollaborationsMemo = new Memo<MemberCollaboration[]>();

  constructor(
    private keycloakService: KeycloakService,
    private impactPanelService: ImpactPanelService,
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.noWallet = false;
    this.loadError = false;

    this.resolveMemberId()
      .then(memberId => {
        if (!memberId) {
          this.noWallet = true;
          this.loading = false;
          return;
        }
        this.impactPanelService.getMemberInfoById(memberId).subscribe({
          next: member => this.onMemberResolved(member),
          error: () => {
            this.loadError = true;
            this.loading = false;
          },
        });
      })
      .catch(() => {
        this.loadError = true;
        this.loading = false;
      });
  }

  // `members/:id` espera el memberId del token (el mismo que usa SuscripcionService), no el
  // id de persona ni `idExterno` del contacto.
  private async resolveMemberId(): Promise<string | undefined> {
    const token = await this.keycloakService.getToken();
    return (jwtDecode(token) as JWTContent).memberId || undefined;
  }

  ngOnDestroy(): void {
    this.panelSub.unsubscribe();
  }

  retry(): void {
    this.ngOnInit();
  }

  private onMemberResolved(member: MemberInfo | null): void {
    const wallet = member?.walletAddress;
    if (!wallet) {
      this.noWallet = true;
      this.loading = false;
      return;
    }
    this.member = member;
    this.walletAddress = wallet;
    this.loadPanel(wallet);
  }

  // Las cuatro llamadas corren en paralelo y cada sección guarda su propio estado; no se
  // espera a colaboraciones (la más lenta) para mostrar la página.
  private loadPanel(wallet: string): void {
    this.panelSub.unsubscribe();
    this.panelSub = new Subscription();
    this.summary = null;
    this.commitments = [];
    this.collaborations = [];
    this.historyEvents = [];
    this.summaryState = 'loading';
    this.commitmentsState = 'loading';
    this.collaborationsState = 'idle';
    this.historyState = 'loading';
    this.commitmentLimit = LIST_PAGE_SIZE;
    this.collaborationLimit = LIST_PAGE_SIZE;

    this.panelSub.add(
      this.impactPanelService.getMemberSummary(wallet).subscribe({
        next: summary => {
          this.summary = summary;
          this.summaryState = 'ready';
          this.revealWhenInitialReady();
        },
        // Sin resumen se ocultan las píldoras y los contadores usan el largo de cada lista.
        error: () => {
          this.summary = null;
          this.summaryState = 'error';
          this.revealWhenInitialReady();
        },
      }),
    );

    this.panelSub.add(
      this.impactPanelService.getMemberCommitments(wallet).subscribe({
        next: commitmentsResp => {
          this.commitments = [...(commitmentsResp.commitments || [])].sort(
            (a, b) => new Date(b.adherenceDate).getTime() - new Date(a.adherenceDate).getTime(),
          );
          this.commitmentsState = 'ready';
          // Los títulos de compromisos se usan en la actividad reciente.
          if (this.historyState === 'ready') this.deriveFromHistory();
          this.revealWhenInitialReady();
        },
        error: () => {
          this.commitmentsState = 'error';
          this.revealWhenInitialReady();
        },
      }),
    );

    this.panelSub.add(
      this.impactPanelService.getUserHistory(wallet, undefined, HISTORY_PAGE_SIZE, 0).subscribe({
        next: historyResp => {
          // Segunda emisión (refresco tras mostrar el cache): si el usuario ya cambió de página o
          // cargó más eventos, se descarta para no devolverlo a la página 1 ni perder lo agregado.
          if (
            this.historyState === 'ready' &&
            (this.activityPage !== 1 ||
              this.loadingMoreHistory ||
              this.historyEvents.length > (historyResp.events || []).length)
          ) {
            return;
          }
          this.historyEvents = historyResp.events || [];
          this.historyOffset = this.historyEvents.length;
          this.historyHasMore = !!historyResp.hasMore;
          this.historyTotalEvents = historyResp.totalEvents || this.historyEvents.length;
          this.activityPage = 1;
          this.pendingActivityPage = null;
          this.deriveFromHistory();
          this.historyState = 'ready';
          this.revealWhenInitialReady();
        },
        error: () => {
          this.historyState = 'error';
          this.revealWhenInitialReady();
        },
      }),
    );

    // Tras un "Reintentar" con la pestaña ya abierta no hay un clic que dispare la carga.
    if (this.activeTab === 'colaboraciones') this.loadCollaborations();
  }

  // Colaboraciones, la llamada más lenta, se pide recién al abrir su pestaña (el contador sale
  // del resumen). Si ya está cargando o cargada no se repite.
  private loadCollaborations(): void {
    if (!this.walletAddress || this.collaborationsState !== 'idle') return;
    this.collaborationsState = 'loading';
    this.panelSub.add(
      this.impactPanelService.getMemberCollaborations(this.walletAddress).subscribe({
        next: collaborationsResp => {
          // Hoy no existe la operación de eliminar una colaboración; si el backend
          // devuelve alguna marcada `deleted` es un dato inconsistente y se oculta.
          this.collaborations = [...(collaborationsResp.collaborations || [])]
            .filter(collaboration => !isGhostMemberCollaboration(collaboration) && !collaboration.deleted)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          this.collaborationsState = 'ready';
        },
        error: () => (this.collaborationsState = 'error'),
      }),
    );
  }

  // La página se muestra de una sola vez cuando termina lo visible al entrar (encabezado,
  // compromisos y actividad), para que no aparezca por partes.
  private revealWhenInitialReady(): void {
    const settled = [this.summaryState, this.commitmentsState, this.historyState].every(
      state => state !== 'loading',
    );
    if (settled) this.loading = false;
  }

  loadMoreHistory(): void {
    if (!this.historyHasMore || this.loadingMoreHistory) return;
    this.loadingMoreHistory = true;
    this.impactPanelService
      .getUserHistory(this.walletAddress, undefined, HISTORY_PAGE_SIZE, this.historyOffset)
      .subscribe({
        next: resp => {
          const newEvents = resp.events || [];
          this.historyEvents = [...this.historyEvents, ...newEvents];
          this.historyOffset += newEvents.length;
          this.historyHasMore = !!resp.hasMore;
          this.historyTotalEvents = resp.totalEvents || this.historyEvents.length;
          if (!newEvents.length) this.historyHasMore = false;
          this.deriveFromHistory();
          this.loadingMoreHistory = false;
          const pending = this.pendingActivityPage;
          this.pendingActivityPage = null;
          if (pending != null) this.goToActivityPage(pending);
        },
        error: () => (this.loadingMoreHistory = false),
      });
  }

  private deriveFromHistory(): void {
    const { collabTitles, commitmentTitles } = collectTitleMaps(this.historyEvents);
    this.collabTitles = collabTitles;
    // Las respuestas de historial no siempre traen `title` en los eventos de tipo
    // PersonCommitment*; se cruza commitmentCode contra la lista de compromisos del
    // miembro (que sí trae title) para resolver el nombre de forma confiable.
    const listTitles: Record<string, string> = {};
    this.commitments.forEach(c => {
      if (c.code != null && c.title) listTitles[c.code] = c.title;
    });
    this.commitmentTitles = { ...commitmentTitles, ...listTitles };
    this.activity = buildActivityFeed(
      this.historyEvents,
      this.walletAddress,
      ACTIVITY_PAGE_SIZE,
      (this.activityPage - 1) * ACTIVITY_PAGE_SIZE,
    );
  }

  get activityTotalCount(): number {
    if (this.historyHasMore) {
      return Math.max(this.historyTotalEvents, this.activity.total);
    }
    return this.activity.total;
  }

  get activityTotalPages(): number {
    return Math.max(1, Math.ceil(this.activityTotalCount / ACTIVITY_PAGE_SIZE));
  }

  get activityRangeLabel(): string {
    if (!this.activity.shown.length) return '0 eventos';
    const start = (this.activityPage - 1) * ACTIVITY_PAGE_SIZE + 1;
    const end = start + this.activity.shown.length - 1;
    return `${start}–${end} de ${this.activityTotalCount}`;
  }

  get activityPageItems(): Array<number | '…'> {
    const total = this.activityTotalPages;
    const current = this.activityPage;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = new Set<number>([1, total, current, current - 1, current + 1]);
    const sorted = [...pages].filter(page => page >= 1 && page <= total).sort((a, b) => a - b);
    const items: Array<number | '…'> = [];
    sorted.forEach((page, idx) => {
      if (idx > 0 && page - sorted[idx - 1] > 1) items.push('…');
      items.push(page);
    });
    return items;
  }

  get canGoActivityPrev(): boolean {
    return this.activityPage > 1 && !this.loadingMoreHistory;
  }

  get canGoActivityNext(): boolean {
    return (
      !this.loadingMoreHistory &&
      (this.activityPage < this.activityTotalPages || this.historyHasMore)
    );
  }

  selectActivityPage(item: number | '…'): void {
    if (item === '…') return;
    this.goToActivityPage(item);
  }

  goToActivityPage(page: number): void {
    if (page < 1 || this.loadingMoreHistory) return;
    const neededOffset = (page - 1) * ACTIVITY_PAGE_SIZE;
    if (this.activity.total > neededOffset) {
      this.activityPage = page;
      this.deriveFromHistory();
      if (this.activity.shown.length < ACTIVITY_PAGE_SIZE && this.historyHasMore) {
        this.pendingActivityPage = page;
        this.loadMoreHistory();
      }
      return;
    }
    if (!this.historyHasMore) {
      const maxLoadedPage = Math.max(1, Math.ceil(this.activity.total / ACTIVITY_PAGE_SIZE));
      this.activityPage = Math.min(page, maxLoadedPage);
      this.deriveFromHistory();
      return;
    }
    this.pendingActivityPage = page;
    this.loadMoreHistory();
  }

  get heroInitials(): string {
    if (!this.member) return '—';
    const name = this.heroDisplayName;
    const initials = name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(w => w[0]?.toUpperCase())
      .join('');
    return initials || (this.isCompany ? 'CO' : 'M');
  }

  get isCompany(): boolean {
    return this.member?.type === 'company';
  }

  get heroDisplayName(): string {
    if (!this.member) return 'Nombre del miembro';
    return this.isCompany
      ? this.member.name
      : `${this.member.name || ''} ${this.member.surname || ''}`.trim();
  }

  get heroMeta(): string {
    if (!this.member) return 'Verificado en Polygon';
    const roleLabel = this.isCompany ? 'Empresa verificada' : 'Persona verificada';
    return this.member.email ? `${roleLabel} · ${this.member.email}` : roleLabel;
  }

  get walletShort(): string {
    return shortAddr(this.walletAddress);
  }

  get polygonscanUrl(): string {
    return `https://polygonscan.com/address/${this.walletAddress}`;
  }

  copyWalletAddress(): void {
    navigator.clipboard?.writeText(this.walletAddress).catch(() => {});
    this.copied = true;
    setTimeout(() => (this.copied = false), 1500);
  }

  switchTab(tab: ImpactPanelTab): void {
    this.activeTab = tab;
    if (tab === 'colaboraciones') this.loadCollaborations();
  }

  trackByCommitment(_index: number, commitment: MemberCommitment): string {
    return `${commitment.code}-${commitment.adherenceDate}`;
  }

  trackByCollaboration(_index: number, collaboration: MemberCollaboration): string {
    return `${collaboration.code}-${collaboration.direction}`;
  }

  activityLabel(event: HistoryEvent): string {
    return eventLabel(event, this.walletAddress, this.collabTitles, this.commitmentTitles);
  }

  activityDotClass(event: HistoryEvent): string {
    return eventDotClass(event);
  }

  activityIconKind(event: HistoryEvent): ActivityIconKind {
    return eventIconKind(event);
  }

  trackByEvent(_index: number, event: HistoryEvent): string {
    return `${event.txHash}-${event.type}-${event.timestamp}`;
  }

  activityStatusChip(event: HistoryEvent): { text: string; cssClass: string } | null {
    return eventStatusChip(event);
  }

  formatDate(iso: string): string {
    return fmtDate(iso);
  }

  formatDateTime(iso: string): string {
    return fmtDateTime(iso);
  }

  formatTime(iso: string): string {
    return fmtTime(iso);
  }

  get hasActiveCommitmentFilters(): boolean {
    return !!(this.commitmentSearch || this.commitmentStatusFilter || this.commitmentCommunityFilter);
  }

  clearCommitmentFilters(): void {
    this.commitmentSearch = '';
    this.commitmentStatusFilter = '';
    this.commitmentCommunityFilter = '';
  }

  get hasActiveCollaborationFilters(): boolean {
    return !!(this.collaborationSearch || this.collaborationStatusFilter || this.collaborationCategoryFilter);
  }

  clearCollaborationFilters(): void {
    this.collaborationSearch = '';
    this.collaborationStatusFilter = '';
    this.collaborationCategoryFilter = '';
  }

  get commitmentStatusOptions(): FilterOption[] {
    return this.commitmentStatusOptionsMemo.get([this.commitments], () =>
      this.computeCommitmentStatusOptions(),
    );
  }

  private computeCommitmentStatusOptions(): FilterOption[] {
    const seen = new Set<string>();
    const options: FilterOption[] = [];
    this.commitments.forEach(c => {
      if (!c.assignedStatus || seen.has(c.assignedStatus)) return;
      seen.add(c.assignedStatus);
      options.push({ value: c.assignedStatus, label: assignedStatusMeta(c.assignedStatus).text });
    });
    return options;
  }

  get commitmentCommunityOptions(): FilterOption[] {
    return this.commitmentCommunityOptionsMemo.get([this.commitments], () =>
      this.computeCommitmentCommunityOptions(),
    );
  }

  private computeCommitmentCommunityOptions(): FilterOption[] {
    const seen = new Set<string>();
    const options: FilterOption[] = [];
    this.commitments.forEach(c => {
      if (!c.community || seen.has(c.community)) return;
      seen.add(c.community);
      options.push({ value: c.community, label: c.community });
    });
    return options;
  }

  get filteredCommitments(): MemberCommitment[] {
    return this.filteredCommitmentsMemo.get(
      [
        this.commitments,
        this.commitmentSearch,
        this.commitmentStatusFilter,
        this.commitmentCommunityFilter,
        this.commitmentSort,
      ],
      () => this.computeFilteredCommitments(),
    );
  }

  get visibleCommitments(): MemberCommitment[] {
    const filtered = this.filteredCommitments;
    return this.visibleCommitmentsMemo.get([filtered, this.commitmentLimit], () =>
      filtered.slice(0, this.commitmentLimit),
    );
  }

  showMoreCommitments(): void {
    this.commitmentLimit += LIST_PAGE_SIZE;
  }

  private computeFilteredCommitments(): MemberCommitment[] {
    const search = this.commitmentSearch.trim().toLowerCase();
    let list = this.commitments.filter(c => {
      if (this.commitmentStatusFilter && c.assignedStatus !== this.commitmentStatusFilter) return false;
      if (this.commitmentCommunityFilter && c.community !== this.commitmentCommunityFilter) return false;
      if (search && !c.title?.toLowerCase().includes(search)) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (this.commitmentSort === 'az') return (a.title || '').localeCompare(b.title || '');
      const diff = new Date(b.adherenceDate).getTime() - new Date(a.adherenceDate).getTime();
      return this.commitmentSort === 'oldest' ? -diff : diff;
    });
    return list;
  }

  get collaborationStatusOptions(): FilterOption[] {
    return this.collaborationStatusOptionsMemo.get([this.collaborations], () =>
      this.computeCollaborationStatusOptions(),
    );
  }

  private computeCollaborationStatusOptions(): FilterOption[] {
    const seen = new Set<string>();
    const options: FilterOption[] = [];
    this.collaborations.forEach(c => {
      const meta = collaborationStatusMeta(c);
      const key = c.status || meta.text;
      if (!key || seen.has(key)) return;
      seen.add(key);
      options.push({ value: key, label: meta.text });
    });
    return options;
  }

  get collaborationCategoryOptions(): FilterOption[] {
    return this.collaborationCategoryOptionsMemo.get([this.collaborations], () =>
      this.computeCollaborationCategoryOptions(),
    );
  }

  private computeCollaborationCategoryOptions(): FilterOption[] {
    const seen = new Set<string>();
    const options: FilterOption[] = [];
    this.collaborations.forEach(c => {
      const key = c.category || '';
      if (!key || seen.has(key)) return;
      seen.add(key);
      options.push({ value: key, label: categoryLabel(key) });
    });
    return options;
  }

  get filteredCollaborations(): MemberCollaboration[] {
    return this.filteredCollaborationsMemo.get(
      [
        this.collaborations,
        this.collaborationSearch,
        this.collaborationStatusFilter,
        this.collaborationCategoryFilter,
        this.collaborationSort,
      ],
      () => this.computeFilteredCollaborations(),
    );
  }

  get visibleCollaborations(): MemberCollaboration[] {
    const filtered = this.filteredCollaborations;
    return this.visibleCollaborationsMemo.get([filtered, this.collaborationLimit], () =>
      filtered.slice(0, this.collaborationLimit),
    );
  }

  showMoreCollaborations(): void {
    this.collaborationLimit += LIST_PAGE_SIZE;
  }

  private computeFilteredCollaborations(): MemberCollaboration[] {
    const search = this.collaborationSearch.trim().toLowerCase();
    let list = this.collaborations.filter(c => {
      if (this.collaborationStatusFilter) {
        const key = c.status || collaborationStatusMeta(c).text;
        if (key !== this.collaborationStatusFilter) return false;
      }
      if (this.collaborationCategoryFilter && c.category !== this.collaborationCategoryFilter) return false;
      if (search && !c.title?.toLowerCase().includes(search)) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (this.collaborationSort === 'az') return (a.title || '').localeCompare(b.title || '');
      const diff = new Date(b.date).getTime() - new Date(a.date).getTime();
      return this.collaborationSort === 'oldest' ? -diff : diff;
    });
    return list;
  }
}
